import {useFormContext} from './useFormContext';

import type {FormValues} from '../sharedTypes/formHelperTypes';

/**
 * A custom hook that retrieves the current form field values from the form context.
 * This hook must be used within a FormProvider component to access the form state.
 * It returns the form values cast to the specified generic type T for type safety.
 *
 * @returns The current form values cast to type T.
 *
 * @throws {Error} When used outside of a FormProvider context.
 *
 * @example
 * ```tsx
 * interface MyFormData {
 *   name: string;
 *   email: string;
 * }
 *
 * const MyComponent = () => {
 *   const values = useFormFieldValues<MyFormData>();
 *
 *   return (
 *     <div>
 *       <p>Name: {values.name}</p>
 *       <p>Email: {values.email}</p>
 *     </div>
 *   );
 * };
 * ```
 */
export const useFormFieldValues = <T = FormValues>() => {
    const formContext = useFormContext();

    if (!formContext) {
        throw new Error('useFormFieldValue must be used within a FormProvider');
    }

    const {form} = formContext;

    return form.values as T;
};